import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import { RegisterSessionEnum } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { TIMETABLE_STRUCTURE } from '@schooly/constants/test-ids';
import { CheckIcon, CrossIcon } from '@schooly/style';
import { FC, FormEvent, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import { ControlModalHeaderInput } from '../../../../../components/uikit-components/Modal/ControlModalHeaderInput';
import {
  ModalConfirm,
  ModalContent,
  ModalFooter,
  ModalMain,
} from '../../../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../../../components/uikit-components/Modal/ModalHeader';
import {
  TimetableStructureContextProps,
  TimetableStructurePeriodForm,
} from '../../../../../context/timetableStructure/WithTimetableStructure';
import { getMinutesDuration, timeToMinutes } from '../../utils';
import { TimetableStructurePeriodAvatar } from './TimetableStructurePeriodAvatar';
import { TimetableStructurePeriodTimeInputs } from './TimetableStructurePeriodTimeInputs';
import {
  PeriodTypeSelection,
  TimetableStructurePeriodTypeSelector,
} from './TimetableStructurePeriodTypeSelector';

const MIN_DURATION_IN_MINUTES = 5;

interface TimetableStructurePeriodModalProps {
  period: TimetableStructurePeriodForm;
  registrationDisabled: boolean;
  confirmChange: TimetableStructureContextProps['confirmChange'];
  onClose: () => void;
  onSubmit: (period: TimetableStructurePeriodForm) => void;
}

export const TimetableStructurePeriodModal: FC<TimetableStructurePeriodModalProps> = ({
  period,
  registrationDisabled,
  confirmChange,
  onClose,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();

  const form = useForm<TimetableStructurePeriodForm>({
    defaultValues: period,
    mode: 'onChange',
  });

  const validateStartTime = useCallback(
    (startTime: string) => {
      if (!startTime) {
        return true;
      }

      const endTime = form.getValues('end_time');
      if (!endTime) {
        return true;
      }

      if (timeToMinutes(endTime) < timeToMinutes(startTime)) {
        return false;
      }

      const durationInMinutes = getMinutesDuration(startTime, endTime);
      if (durationInMinutes < MIN_DURATION_IN_MINUTES) {
        return false;
      }

      return true;
    },
    [form],
  );

  const validateEndTime = useCallback(
    (endTime: string) => {
      if (!endTime) {
        return true;
      }

      const startTime = form.getValues('start_time');
      if (!startTime) {
        return true;
      }

      if (timeToMinutes(endTime) < timeToMinutes(startTime)) {
        return formatMessage({ id: 'school-timetableStructure-Error-TimeMax' });
      }

      const durationInMinutes = getMinutesDuration(startTime, endTime);
      if (durationInMinutes < MIN_DURATION_IN_MINUTES) {
        return formatMessage(
          { id: 'school-timetableStructure-Error-TimeMin' },
          { minutes: MIN_DURATION_IN_MINUTES },
        );
      }

      return true;
    },
    [form, formatMessage],
  );

  const periodType = form.watch('type');
  const registerSession = form.watch('register_session');
  const isRegister = registerSession != null;

  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (!values.start_time || !values.end_time) {
        return;
      }

      if (name === 'start_time') {
        form.trigger('end_time');
      }
      if (name === 'end_time') {
        form.trigger('start_time');
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const handleSelectionChange = useCallback(
    async ({ type: newType, isRegister: newIsRegister }: PeriodTypeSelection) => {
      if (period && newType !== form.getValues('type')) {
        const confirmed = await confirmChange({
          type: 'change_period_type',
          id: period.id,
          newType,
        });
        if (!confirmed) return;
      }
      const current = form.getValues('register_session');
      form.setValue('type', newType, { shouldDirty: true, shouldValidate: true });
      form.setValue(
        'register_session',
        newIsRegister ? (current ?? RegisterSessionEnum.Am) : null,
        {
          shouldDirty: true,
        },
      );
    },
    [period, confirmChange, form],
  );

  const handleClose = useCallback(async () => {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }

    const isConfirmed = await getConfirmation({
      textId: 'school-edit-CloseUnsavedConfirmation',
    });

    if (isConfirmed) {
      onClose();
    }
  }, [form.formState.isDirty, getConfirmation, onClose]);

  const handleFormSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.stopPropagation();
      form.handleSubmit(onSubmit)(e);
    },
    [form, onSubmit],
  );

  return (
    <ModalConfirm open fullWidth onClose={handleClose}>
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <ModalHeader
            active
            title={
              <Stack direction="row" alignItems="center" gap={2.5}>
                <Tooltip
                  title={formatMessage({
                    id: `school-timetableStructure-periodType-${periodType}`,
                  })}
                  componentsProps={{ tooltip: { sx: { p: 1.25, minWidth: 200 } } }}
                >
                  <div>
                    <TimetableStructurePeriodAvatar
                      type={periodType}
                      registerSession={registerSession}
                    />
                  </div>
                </Tooltip>
                <ControlModalHeaderInput
                  control={form.control}
                  name="name"
                  rules={{ required: true }}
                  placeholder={formatMessage({ id: 'school-timetableStructure-PeriodName' })}
                  autoFocus
                  inputProps={{ 'data-test-id': TIMETABLE_STRUCTURE.PERIOD_NAME_INPUT }}
                />
              </Stack>
            }
          >
            <IconButton onClick={handleClose}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>

          <ModalMain>
            <ModalContent active>
              <Stack gap={2.5}>
                <TimetableStructurePeriodTimeInputs
                  validateStartTime={validateStartTime}
                  validateEndTime={validateEndTime}
                />

                <TimetableStructurePeriodTypeSelector
                  type={periodType}
                  isRegister={isRegister}
                  registrationDisabled={registrationDisabled}
                  onChange={handleSelectionChange}
                />
              </Stack>
            </ModalContent>
          </ModalMain>

          <ModalFooter justifyContent="flex-end" gap={1.25} active>
            <Button variant="outlined" onClick={onClose}>
              <FormattedMessage id="action-Cancel" />
            </Button>
            <Button type="submit" endIcon={<CheckIcon />}>
              <FormattedMessage id="action-Save" />
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalConfirm>
  );
};
